import { useCallback, useEffect, useState } from "react";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { HttpApiService } from "@/shared/services/httpApi.service";

import { PaymentsPayinService, type PayinRuntimeEntity } from "../services/paymentsPayin.service";

/**
 * Session-scoped access latch. A missing `payments:read` role surfaces as a 401 (the RBAC guard throws
 * Unauthorized, not Forbidden), and every 401 costs a silent `/auth/refresh` + replay inside HttpApiService —
 * after the first denial we stop asking for the rest of the session. Keyed by user+org so a re-login probes
 * again.
 */
const deniedPrincipals = new Set<string>();

const DEPOSITS_LIMIT = 5;

export interface UseRuntimeDepositsResult {
  deposits: PayinRuntimeEntity[];
  isLoading: boolean;
  /** False after a 401/403 — callers hide the list entirely (no toast, no error state). */
  hasAccess: boolean;
  /** True when the initial fetch failed for a non-access reason — never render an empty list as truth. */
  hasError: boolean;
  refresh: () => Promise<void>;
}

/**
 * Latest payins detected by the payments runtime for the current organization and country — used by the
 * Mexico deposit panel to show incoming SPEI deposits arriving/confirming (the runtime detects them via the
 * provider webhook; there is no user-initiated create). Fetch is skipped without an organization id: an
 * unscoped query would return other organizations' payins to an INTERNAL user.
 */
export const useRuntimeDeposits = (country: string, enabled = true): UseRuntimeDepositsResult => {
  const { user } = useAuth();
  const organizationId = user?.organization_id ?? user?.organization?.id ?? null;
  const deniedKey = `${user?.id ?? "anon"}:${organizationId ?? "no-org"}`;
  const shouldFetch = enabled && !!organizationId && !deniedPrincipals.has(deniedKey);

  const [deposits, setDeposits] = useState<PayinRuntimeEntity[]>([]);
  // Loading is DERIVED (fetch wanted but not yet settled for this org+country) instead of a flag set
  // synchronously in effects — `enabled` can flip true after mount without leaving a stale `false`.
  const [settledKey, setSettledKey] = useState<string | null>(null);
  const [hasAccess, setHasAccess] = useState(() => !deniedPrincipals.has(deniedKey));
  const [hasError, setHasError] = useState(false);

  const fetchKey = `${organizationId ?? "no-org"}:${country}`;
  const isLoading = shouldFetch && settledKey !== fetchKey;

  useEffect(() => {
    if (!enabled || !organizationId || deniedPrincipals.has(deniedKey)) return;
    let cancelled = false;
    const key = `${organizationId}:${country}`;

    PaymentsPayinService.listPayins({ organizationId, country, limit: DEPOSITS_LIMIT })
      .then((response) => {
        if (cancelled) return;
        setDeposits(response.data.data);
        setHasError(false);
        setSettledKey(key);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (HttpApiService.isAccessDeniedError(err)) {
          deniedPrincipals.add(deniedKey);
          setHasAccess(false);
        } else {
          // Money display rule: a failed read must hide the list, never show "no deposits" as truth.
          setHasError(true);
        }
        setDeposits([]);
        setSettledKey(key);
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, organizationId, country, deniedKey]);

  /**
   * Silent refetch (no skeleton flash). On a transient failure it keeps the last known-good list; only an
   * access denial hides the section (and latches).
   */
  const refresh = useCallback(async () => {
    if (!enabled || !organizationId || deniedPrincipals.has(deniedKey)) return;
    try {
      const response = await PaymentsPayinService.listPayins({
        organizationId,
        country,
        limit: DEPOSITS_LIMIT,
      });
      setDeposits(response.data.data);
      setHasError(false);
      setSettledKey(`${organizationId}:${country}`);
    } catch (err: unknown) {
      if (HttpApiService.isAccessDeniedError(err)) {
        deniedPrincipals.add(deniedKey);
        setHasAccess(false);
      }
      // Non-access refresh errors: keep the previous list (last known good).
    }
  }, [enabled, organizationId, country, deniedKey]);

  return { deposits, isLoading, hasAccess, hasError, refresh };
};
